/**
 * SellerMap — embedded 2GIS MapGL map for the seller-detail surface
 * (STORY-003-004).
 *
 * 2GIS is the ONLY map provider in GardEn (Mapbox / Google are forbidden —
 * REQ-TRD-§5.3-A). The MapGL SDK is lazy-loaded from the 2GIS CDN via a
 * programmatic <script> append (NOT an inline <script>, NOT in index.html)
 * so strict CSP is satisfied with `script-src 'self' <mapgl-origin>`
 * (Addendum §7.9). The component is loaded only on the seller-detail route
 * (the consumer code-splits it via React.lazy — STORY-003-006) so the
 * marketplace browse bundle never carries the SDK loader (REQ-TRD-§5.2-A).
 *
 * Map behaviour (story Implementation logic):
 *   - center: seller location when zoomed past street level (zoom 16+),
 *     else the midpoint of seller + market center;
 *   - zoom 17 (pavilion-level);
 *   - a pin at (sellerLat, sellerLng); clicking it opens a popup with the
 *     pavilion number + an optional photo thumbnail;
 *   - "Get directions" button → build2GisLinks (STORY-003-003) → dgis://
 *     on mobile with an https://2gis.kg fallback after a timeout, or the
 *     web URL directly on desktop.
 *
 * Failure → a `role="alert"` with the localized error and a static
 * fallback link `https://2gis.kg/geo/{lng},{lat}` (NB: lng,lat order).
 *
 * Tokens come from the `--g-*` Tailwind mirror; strings from the
 * `marketplace` i18n namespace (`map.*`). No hand-rolled colors, no
 * hardcoded copy.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, RotateCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { build2GisLinks, trackContactClick } from "@/lib/deep-links";
import type { LngLat, MapGLMap } from "@/types/2gis";

/** The 2GIS MapGL CDN entry point. Allow-listed in CSP `script-src`. */
const SDK_URL = "https://mapgl.2gis.com/api/js/v1";
/** Pavilion-level zoom (story Implementation logic). */
const MAP_ZOOM = 17;
/** At/above this zoom we center on the seller, not the seller↔market midpoint. */
const STREET_LEVEL_ZOOM = 16;
/** Skeleton → spinner threshold (ms). */
const SPINNER_AFTER_MS = 800;
/** Mobile deep-link fallback delay (ms). */
const DIRECTIONS_FALLBACK_MS = 1000;
/** Below this elapsed time we treat the app-scheme handoff as failed. */
const DIRECTIONS_HANDOFF_MIN_MS = 800;

export interface SellerMapProps {
  /** Opaque seller id — used only for the PII-free analytics event. */
  sellerId: string;
  sellerLat: number;
  sellerLng: number;
  marketCenterLat: number;
  marketCenterLng: number;
  /** Market name — pin tooltip + screen-reader label. */
  marketName: string;
  pavilionNumber: string;
  /** Small thumb shown in the pin popup. */
  pavilionPhotoUrl?: string;
  className?: string;
  /** Fired once when the map is ready (analytics hook). */
  onReady?: () => void;
}

/** A `[lng, lat]` MapGL coordinate (GeoJSON order) from `(lat, lng)`. */
function toLngLat(lat: number, lng: number): LngLat {
  return [lng, lat];
}

/** Static 2GIS web fallback for a coordinate. NB: `geo/{lng},{lat}`. */
function staticGeoUrl(lat: number, lng: number): string {
  return `https://2gis.kg/geo/${lng},${lat}`;
}

/**
 * Heuristic: is this a mobile user agent? Prefers the structured
 * User-Agent Client Hint, falls back to the UA-string regex. Returns
 * `false` outside a DOM context. Mirrors the helper in `@/lib/deep-links`
 * so the directions dance keys off the same signal.
 */
function isMobileUserAgent(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const uaData = (
    navigator as Navigator & { userAgentData?: { mobile?: boolean } }
  ).userAgentData;
  if (uaData && typeof uaData.mobile === "boolean") {
    return uaData.mobile;
  }
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

type MapStatus = "loading" | "ready" | "error";

export default function SellerMap({
  sellerId,
  sellerLat,
  sellerLng,
  marketCenterLat,
  marketCenterLng,
  marketName,
  pavilionNumber,
  pavilionPhotoUrl,
  className,
  onReady,
}: SellerMapProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapGLMap | null>(null);
  const [status, setStatus] = useState<MapStatus>("loading");
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  // Bump to force the load effect to re-run on "Try again".
  const [reloadKey, setReloadKey] = useState<number>(0);

  // Skeleton → spinner after 800ms (story loading state).
  useEffect(() => {
    if (status !== "loading") {
      return;
    }
    setShowSpinner(false);
    const timer = window.setTimeout(() => setShowSpinner(true), SPINNER_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [status, reloadKey]);

  // Load the SDK (once) and initialize the map.
  useEffect(() => {
    let cancelled = false;

    const initMap = (): void => {
      const mapgl = window.mapgl;
      const container = containerRef.current;
      if (!mapgl || !container || cancelled) {
        return;
      }
      try {
        // Center on the seller at street level (zoom 16+), else the
        // seller↔market midpoint (story Implementation logic).
        const center: LngLat =
          MAP_ZOOM >= STREET_LEVEL_ZOOM
            ? toLngLat(sellerLat, sellerLng)
            : toLngLat(
                (sellerLat + marketCenterLat) / 2,
                (sellerLng + marketCenterLng) / 2,
              );
        const apiKey = import.meta.env.VITE_2GIS_API_KEY;
        const map = new mapgl.Map(container, {
          center,
          zoom: MAP_ZOOM,
          ...(typeof apiKey === "string" && apiKey.length > 0
            ? { key: apiKey }
            : {}),
        });
        mapRef.current = map;

        // Pin at the seller's pavilion; click → popup (pavilion + photo).
        const marker = new mapgl.Marker(map, {
          coordinates: toLngLat(sellerLat, sellerLng),
          label: { text: marketName },
        });
        marker.on("click", () => {
          if (cancelled) {
            return;
          }
          const popup = document.createElement("div");
          popup.className =
            "flex flex-col gap-1 rounded-[10px] bg-card p-2 text-card-foreground shadow";
          popup.setAttribute("data-testid", "seller-map-popup");
          const number = document.createElement("p");
          number.className = "text-sm font-medium";
          number.textContent = pavilionNumber;
          popup.appendChild(number);
          if (pavilionPhotoUrl) {
            const img = document.createElement("img");
            img.src = pavilionPhotoUrl;
            img.alt = pavilionNumber;
            img.className = "h-16 w-16 rounded-md object-cover";
            img.setAttribute("data-testid", "seller-map-popup-photo");
            popup.appendChild(img);
          }
          new mapgl.HtmlMarker(map, {
            coordinates: toLngLat(sellerLat, sellerLng),
            html: popup,
          });
        });

        if (!cancelled) {
          setStatus("ready");
          onReady?.();
        }
      } catch {
        if (!cancelled) {
          setStatus("error");
        }
      }
    };

    if (window.mapgl) {
      initMap();
      return () => {
        cancelled = true;
        mapRef.current?.destroy();
        mapRef.current = null;
      };
    }

    // No inline script: programmatically append an external-src <script>.
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => {
      if (!cancelled) {
        initMap();
      }
    };
    script.onerror = () => {
      if (!cancelled) {
        setStatus("error");
      }
    };
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      script.onload = null;
      script.onerror = null;
      mapRef.current?.destroy();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const handleDirections = (): void => {
    const links = build2GisLinks({
      lat: sellerLat,
      lng: sellerLng,
      placeName: marketName,
    });
    if (!links) {
      return;
    }
    trackContactClick("2gis", sellerId);

    if (!isMobileUserAgent()) {
      // Desktop: navigate straight to the web URL — no timeout fallback.
      window.location.href = links.fallback;
      return;
    }

    // Mobile: try the dgis:// primary, fall back to the web URL if the
    // app handoff didn't take us away from the page (story pseudocode).
    const start = Date.now();
    window.location.href = links.primary;
    window.setTimeout(() => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - start > DIRECTIONS_HANDOFF_MIN_MS
      ) {
        window.location.href = links.fallback;
      }
    }, DIRECTIONS_FALLBACK_MS);
  };

  const handleRetry = (): void => {
    setStatus("loading");
    setReloadKey((k) => k + 1);
  };

  if (status === "error") {
    return (
      <div
        role="alert"
        data-testid="seller-map-error"
        className={`flex flex-col items-start gap-3 rounded-[14px] border border-border bg-muted p-4 text-sm text-foreground ${className ?? ""}`}
      >
        <p>{t("map.load_failed")}</p>
        <a
          href={staticGeoUrl(sellerLat, sellerLng)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="seller-map-static-fallback"
          className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
        >
          <MapPin aria-hidden="true" className="size-4" />
          {t("map.open_in_2gis")}
        </a>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRetry}
          data-testid="seller-map-retry"
        >
          <RotateCw aria-hidden="true" className="size-4" />
          {t("map.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-3 ${className ?? ""}`}>
      <div className="relative h-64 w-full overflow-hidden rounded-[14px]">
        <div
          ref={containerRef}
          role="application"
          aria-label={t("map.aria_label", { market: marketName })}
          data-testid="seller-map-container"
          className="size-full bg-secondary"
        />
        {status === "loading" && (
          <div
            role="status"
            aria-busy="true"
            aria-label={t("map.loading")}
            data-testid="seller-map-skeleton"
            className="absolute inset-0 flex items-center justify-center bg-secondary"
          >
            {showSpinner && (
              <RotateCw
                aria-hidden="true"
                className="size-6 animate-spin text-muted-foreground"
                data-testid="seller-map-spinner"
              />
            )}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        onClick={handleDirections}
        data-testid="seller-map-directions"
        aria-label={t("aria.directions", {
          market: marketName,
          pavilion: pavilionNumber,
        })}
      >
        <MapPin aria-hidden="true" className="size-4" />
        {t("map.directions")}
      </Button>
    </div>
  );
}
